import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Lock as LockIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface AdvancedReportingSettingsProps {
  salonId: string;
}

const AdvancedReportingSettings: React.FC<AdvancedReportingSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('advanced_reporting', 'write');
  const restrictions = getRestrictions('advanced_reporting');

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/advanced_reporting`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load advanced reporting settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Advanced Reporting Settings</Typography>
        <Chip label="Super Admin Only" color="primary" size="small" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Custom Reports
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.customReports?.enabled || false}
                    disabled={!canWrite}
                  />
                }
                label="Enable Custom Reports"
              />
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Max Reports: {settings?.customReports?.maxReports || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdvancedReportingSettings;